import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useAuth } from 'context/user'
import { AuthService } from 'utility/services/auth'
import { SubscriptionService } from 'utility/services/subscription'
import {
    normalizeUsageSnapshot,
    UNKNOWN_USAGE_SNAPSHOT,
    isFreeTier,
    getWorstTone,
    isLoginServicePlanFree,
    isPaymentStatusPaid,
    paymentPlanLabel,
    buildProvisionalFreeUsage,
    asFreeDisplayUsage,
} from 'utility/subscription-usage'
import { subscribePlanLimitBlocked } from 'utility/plan-limit-events'

const SubscriptionUsageContext = createContext(null)

export const SubscriptionUsageProvider = ({ children }) => {
    const { isAuthenticated } = useAuth()
    const [usage, setUsage] = useState(null)
    const [loading, setLoading] = useState(false)
    const [paidMember, setPaidMember] = useState(null)
    const [planLabel, setPlanLabel] = useState('')
    const [blockPayload, setBlockPayload] = useState(null)
    const inFlightRef = useRef(null)
    const lastFetchAtRef = useRef(0)
    const usageRef = useRef(null)

    useEffect(() => {
        usageRef.current = usage
    }, [usage])

    const fetchUsage = useCallback(async ({ force = false } = {}) => {
        if (!isAuthenticated) {
            setUsage(null)
            return null
        }
        const now = Date.now()
        // Dedupe: reuse in-flight request; skip focus spam within 15s unless forced.
        if (inFlightRef.current) {
            return inFlightRef.current
        }
        if (!force && lastFetchAtRef.current && now - lastFetchAtRef.current < 15000) {
            return usageRef.current
        }
        setLoading(true)
        const request = (async () => {
            try {
                const raw = await SubscriptionService.getUsage()
                const normalized = normalizeUsageSnapshot(raw)
                setUsage(normalized)
                lastFetchAtRef.current = Date.now()
                SubscriptionService.getPaymentStatus()
                    .then(data => {
                        if (data && typeof data === 'object') {
                            setPaidMember(isPaymentStatusPaid(data))
                            setPlanLabel(paymentPlanLabel(data))
                        }
                    })
                    .catch(() => {})
                return normalized
            } catch (err) {
                // Usage API not live yet (e.g. QA 404): never crash the app.
                // If login ServicePlan says Free, show provisional Free UI so
                // widgets/placement can be reviewed before backend ships.
                const user = AuthService.getUser()
                const fallback = isLoginServicePlanFree(user)
                    ? buildProvisionalFreeUsage()
                    : { ...UNKNOWN_USAGE_SNAPSHOT }
                setUsage(fallback)
                lastFetchAtRef.current = Date.now()
                return fallback
            } finally {
                setLoading(false)
                inFlightRef.current = null
            }
        })()
        inFlightRef.current = request
        return request
    }, [isAuthenticated])

    useEffect(() => {
        if (isAuthenticated) {
            fetchUsage({ force: true })
        } else {
            setUsage(null)
            setPaidMember(null)
            setPlanLabel('')
            setBlockPayload(null)
            lastFetchAtRef.current = 0
        }
    }, [isAuthenticated, fetchUsage])

    useEffect(() => {
        if (!isAuthenticated) return undefined
        let cancelled = false
        SubscriptionService.getPaymentStatus()
            .then(data => {
                if (cancelled || !data || typeof data !== 'object') return
                setPaidMember(isPaymentStatusPaid(data))
                setPlanLabel(paymentPlanLabel(data))
            })
            .catch(() => {
                if (!cancelled) {
                    setPaidMember(null)
                    setPlanLabel('')
                }
            })
        return () => {
            cancelled = true
        }
    }, [isAuthenticated])

    useEffect(() => {
        if (!isAuthenticated) return undefined
        const onFocus = () => {
            fetchUsage({ force: false })
        }
        window.addEventListener('focus', onFocus)
        return () => window.removeEventListener('focus', onFocus)
    }, [isAuthenticated, fetchUsage])

    useEffect(() => {
        return subscribePlanLimitBlocked(payload => {
            setBlockPayload(payload)
        })
    }, [])

    const clearBlockDialog = useCallback(() => {
        setBlockPayload(null)
    }, [])

    const showBlockDialog = useCallback(payload => {
        setBlockPayload(payload)
    }, [])

    // Stable refs — must NOT be recreated when usage/loading changes, or
    // screens that `useEffect(..., [refetchUsage])` will loop forever.
    const refetchUsage = useCallback(
        () => fetchUsage({ force: true }),
        [fetchUsage],
    )
    const fetchUsageForced = useCallback(
        () => fetchUsage({ force: true }),
        [fetchUsage],
    )

    const contextData = useMemo(() => {
        const tier = usage?.tier
        const loginFree = isLoginServicePlanFree(AuthService.getUser())
        const paid = paidMember === true
        const unpaid = paidMember === false
        const showFreeUi =
            Boolean(usage) &&
            !paid &&
            (isFreeTier(tier) || unpaid || loginFree)
        const freeUsage = showFreeUi ? asFreeDisplayUsage(usage) : null
        const free = Boolean(freeUsage)
        return {
            usage,
            freeUsage,
            loading,
            fetchUsage: fetchUsageForced,
            refetchUsage,
            isFreePlan: free,
            planLabel,
            worstTone: free ? getWorstTone(freeUsage) : 'ok',
            blockPayload,
            clearBlockDialog,
            showBlockDialog,
            upgradeUrl: usage?.upgradeUrl,
        }
    }, [
        usage,
        planLabel,
        paidMember,
        loading,
        fetchUsageForced,
        refetchUsage,
        blockPayload,
        clearBlockDialog,
        showBlockDialog,
    ])

    return (
        <SubscriptionUsageContext.Provider value={contextData}>
            {children}
        </SubscriptionUsageContext.Provider>
    )
}

export const useSubscriptionUsage = () => {
    const ctx = useContext(SubscriptionUsageContext)
    if (!ctx) {
        return {
            usage: null,
            freeUsage: null,
            loading: false,
            fetchUsage: async () => null,
            refetchUsage: async () => null,
            isFreePlan: false,
            planLabel: '',
            worstTone: 'ok',
            blockPayload: null,
            clearBlockDialog: () => {},
            showBlockDialog: () => {},
            upgradeUrl: null,
        }
    }
    return ctx
}
